import { useGetCurrenciesQuery } from '@/api/currencies'
import { useGetCustomerQuery } from '@/api/customer'
import { selectCurrency, selectIncludeVat } from '@/features/app/appSlice'
import { formatPrice } from '@/utils/helpers'
import { useAppSelector } from '@/utils/hooks/useAppSelector'
import { useMemo } from 'react'

interface UsePriceOptions {
  /**
   * Override the currency code used for the symbol.
   * When set, currency conversion is skipped (the price is assumed
   * to already be in the target currency).
   */
  currencyCode?: string
}

/**
 * Custom hook to get price with currency symbol and calculated VAT.
 */
const usePrice = (options?: UsePriceOptions) => {
  const { currencyCode: overrideCurrency } = options ?? {}
  const { data: currencies } = useGetCurrenciesQuery()
  const selectedCurrency = useAppSelector(selectCurrency)
  const includeVat = useAppSelector(selectIncludeVat)
  const { data: customer } = useGetCustomerQuery()

  const displayCurrency = overrideCurrency ?? selectedCurrency

  const currencyRate = useMemo(() => {
    return currencies
      ?.find((c) => c.currencyCode === selectedCurrency)
      ?.currencyRate ?? null
  }, [currencies, selectedCurrency])

  const convertPrice = (amount: number): number => {
    if (overrideCurrency) return amount
    return amount / currencyRate!
  }

  const format = (amount: number, showSymbol?: boolean): string => {
    const formatted = formatPrice(amount)
    return showSymbol ? `${formatted} ${displayCurrency}` : formatted
  }

  /**
   * Get price with currency symbol and calculated VAT.
   */
  const getPrice = (price?: number, showSymbol?: boolean): string => {
    if (!price || (!overrideCurrency && !currencyRate))
      return '-'

    const vatPercent = customer?.vatPercentage ?? 25
    let calculated = price

    if (!includeVat)
      calculated = calculated / (vatPercent / 100 + 1)

    return format(convertPrice(calculated), showSymbol)
  }

  /**
   * Get formatted price based on selected currency, without VAT calculation.
   */
  const getFormattedPrice = (price?: number, showSymbol?: boolean): string => {
    if (!price || (!overrideCurrency && !currencyRate))
      return '-'

    return format(convertPrice(price), showSymbol)
  }

  return {
    price: getPrice,
    formatPrice: getFormattedPrice
  }
}

export default usePrice
